import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import { apiClient } from "../../utils/apiClient";
import type {
  CheckEmailCandidatesRequest,
  CheckEmailCandidatesResponse,
  ExecuteEmailActionsRequest,
  ExecuteEmailActionsResponse,
  EmailCandidate,
} from "@services/shipments/types";
import { useDispatch } from "react-redux";
import { openEmailModal } from "@/store/adminSlice";

interface AddOrRemoveEmailActionProps {
  shipmentId: string;
  tags: string[];
  onSuccess?: () => void;
  disabled?: boolean;
}

export function AddOrRemoveEmailAction({
  shipmentId,
  tags,
  onSuccess,
  disabled = false,
}: AddOrRemoveEmailActionProps) {
  const dispatch = useDispatch();

  // Email management state
  const [loadingEmailCandidates, setLoadingEmailCandidates] = useState(false);
  const [emailCandidates, setEmailCandidates] = useState<EmailCandidate[]>([]);
  const [selectedEmailChanges, setSelectedEmailChanges] = useState<Set<string>>(
    new Set()
  );
  const [executingEmailActions, setExecutingEmailActions] = useState(false);
  const [emailActionsResult, setEmailActionsResult] =
    useState<ExecuteEmailActionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPerformedCheck, setHasPerformedCheck] = useState(false);

  const handleLoadEmailCandidates = async () => {
    try {
      setLoadingEmailCandidates(true);
      setError(null);
      setEmailActionsResult(null);

      const request: CheckEmailCandidatesRequest = {
        shipmentId,
      };

      const response = await apiClient.post<CheckEmailCandidatesResponse>(
        "/shipments/check-email-candidates",
        request
      );

      setEmailCandidates(response.emailCandidates);
      setHasPerformedCheck(true);
      // Pre-select all candidates by default
      const allCandidateIds = new Set(
        response.emailCandidates.map((c) => c.shipmentEmailId)
      );
      setSelectedEmailChanges(allCandidateIds);
    } catch (err) {
      console.error("Error loading email candidates:", err);
      setError("Failed to load email candidates");
    } finally {
      setLoadingEmailCandidates(false);
    }
  };

  const handleEmailCheckboxChange = (
    shipmentEmailId: string,
    action: "add" | "remove"
  ) => {
    const newSelected = new Set(selectedEmailChanges);

    // Toggle selection for this email
    if (newSelected.has(shipmentEmailId)) {
      newSelected.delete(shipmentEmailId);
    } else {
      newSelected.add(shipmentEmailId);
    }

    setSelectedEmailChanges(newSelected);
  };

  const handleExecuteEmailActions = async () => {
    if (selectedEmailChanges.size === 0) {
      setError("No email changes selected");
      return;
    }

    try {
      setExecutingEmailActions(true);
      setError(null);

      const actions = Array.from(selectedEmailChanges).map(
        (shipmentEmailId) => {
          const candidate = emailCandidates.find(
            (c) => c.shipmentEmailId === shipmentEmailId
          );
          return {
            shipmentEmailId,
            action: candidate?.action || "add",
          };
        }
      );

      const request: ExecuteEmailActionsRequest = {
        shipmentId,
        actions,
      };

      const response = await apiClient.post<ExecuteEmailActionsResponse>(
        "/shipments/execute-email-actions",
        request
      );

      setEmailActionsResult(response);

      if (response.status === "ok") {
        // Clear the current candidates since actions were applied
        setEmailCandidates([]);
        setSelectedEmailChanges(new Set());
        setHasPerformedCheck(false);
        onSuccess?.();
      }
    } catch (err) {
      console.error("Error executing email actions:", err);
      setError("Failed to execute email actions");
    } finally {
      setExecutingEmailActions(false);
    }
  };

  const handleOpenEmail = (emailId: string, eventName: string) => {
    dispatch(openEmailModal(emailId));
  };

  return (
    <Stack spacing={2} width={"100%"}>
      {/* Action Button */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          onClick={handleLoadEmailCandidates}
          disabled={disabled || loadingEmailCandidates}
          color="primary"
          variant="outlined"
          size="small"
        >
          {loadingEmailCandidates ? (
            <CircularProgress size={20} />
          ) : (
            "Check Emails to Add/Remove"
          )}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Email Candidates */}
      {emailCandidates.length > 0 ? (
        <Alert severity="info" onClose={() => setEmailCandidates([])}>
          <Typography variant="subtitle2">Email Management:</Typography>
          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            Select emails to add or remove from this shipment:
          </Typography>

          <List dense>
            {emailCandidates.map((candidate) => (
              <ListItem key={candidate.shipmentEmailId} disablePadding>
                <ListItemIcon>
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ ml: 4 }}
                        checked={selectedEmailChanges.has(
                          candidate.shipmentEmailId
                        )}
                        onChange={() =>
                          handleEmailCheckboxChange(
                            candidate.shipmentEmailId,
                            candidate.action
                          )
                        }
                        size="small"
                      />
                    }
                    label=""
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: "pointer",
                          color: "primary.main",
                          textDecoration: "underline",
                        }}
                        onClick={() =>
                          handleOpenEmail(
                            candidate.emailId,
                            candidate.emailSubject || "Email"
                          )
                        }
                      >
                        {candidate.emailSubject || "No Subject"}
                      </Typography>
                      <Chip
                        label={candidate.action === "remove" ? "Remove" : "Add"}
                        color={
                          candidate.action === "remove" ? "error" : "success"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      {candidate.emailFrom && (
                        <Typography variant="caption">
                          From: {candidate.emailFrom.join(", ")}
                        </Typography>
                      )}
                      {candidate.sentTime && (
                        <Typography variant="caption" display="block">
                          Sent: {new Date(candidate.sentTime).toLocaleString()}
                        </Typography>
                      )}
                      {candidate.attachmentCount > 0 && (
                        <Typography variant="caption" display="block">
                          Attachments: {candidate.attachmentCount}
                        </Typography>
                      )}
                      {candidate.reason && (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ fontStyle: "italic", color: "text.secondary" }}
                        >
                          Reason: {candidate.reason}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {selectedEmailChanges.size > 0 && (
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Button
                onClick={handleExecuteEmailActions}
                disabled={executingEmailActions}
                color="primary"
                variant="contained"
                size="small"
              >
                {executingEmailActions ? (
                  <CircularProgress size={20} />
                ) : (
                  `Apply Changes (${selectedEmailChanges.size})`
                )}
              </Button>
              <Button
                onClick={() => setSelectedEmailChanges(new Set())}
                disabled={executingEmailActions}
                color="secondary"
                variant="outlined"
                size="small"
              >
                Clear All
              </Button>
            </Box>
          )}
        </Alert>
      ) : (
        // Show message when check was performed but no candidates found
        hasPerformedCheck &&
        emailCandidates.length === 0 &&
        !loadingEmailCandidates &&
        !error && (
          <Alert
            severity="info"
            onClose={() => {
              setEmailCandidates([]);
              setHasPerformedCheck(false);
            }}
          >
            <Typography variant="subtitle2">Email Management:</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              No email candidates found to add or remove from this shipment.
            </Typography>
          </Alert>
        )
      )}

      {/* Email Actions Result */}
      {emailActionsResult && (
        <Alert
          severity={emailActionsResult.status === "ok" ? "success" : "error"}
          onClose={() => setEmailActionsResult(null)}
        >
          <Typography variant="subtitle2">Email Actions Result:</Typography>
          <Typography variant="body2">{emailActionsResult.message}</Typography>
          {emailActionsResult.errors &&
            emailActionsResult.errors.length > 0 && (
              <>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: "bold" }}>
                  Errors:
                </Typography>
                {emailActionsResult.errors.map((error, index) => (
                  <Typography key={index} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </>
            )}
        </Alert>
      )}
    </Stack>
  );
}
